import { Polygon } from 'geojson';

import { ActionEnum, Coordinates, Coordinates3D } from '../commons/commons.types';
import { Identification } from './identification.types';

export enum LocationTypeEnum {
  LINEAR = 'LINEAR',
  PLANAR = 'PLANAR',
  SPATIAL = 'SPATIAL',
  SPATIAL_LINEAR = 'SPATIAL_LINEAR',
  POLYGON = 'POLYGON',
  CARTESIAN = 'CARTESIAN',
  TRACKER = 'TRACKER',
}

export const SENSOR_LABEL_ICON_OFFSET_PX = [0, 45];
export const SENSOR_LABEL_TEXT_OFFSET_PX_L = [0, 3.3];
export const SENSOR_LABEL_TEXT_OFFSET_PX_M = [0, 2.6];
export const CAMERA_PERIM_LABEL_TEXT_OFFSET_PX = [0, 0.1];

export interface PlanarLocation extends PlanarLocationOptions {
  type: LocationTypeEnum.PLANAR;
}

export interface CartesianLocationOptions {
  x: number;
  y: number;
}

export interface CartesianLocation extends CartesianLocationOptions {
  type: LocationTypeEnum.CARTESIAN;
}

export interface TrackerLocationOptions extends CartesianLocationOptions {
  width: number;
  height: number;
  id?: number;
  label?: string;
  score?: number;
}

export interface TrackerLocation extends TrackerLocationOptions {
  type: LocationTypeEnum.TRACKER;
}

export interface PlanarLocationOptions {
  position: Coordinates | null;
  horizontalPrecision: number | null;
  speed: number | null;
  heading: number | null;
  /** Horizontal speed direction towards North. In range [0, 360[. */
  yaw: number | null;
}

export interface LinearLocation extends LinearLocationOptions {
  type: LocationTypeEnum.LINEAR;
}

export interface LinearLocationOptions {
  center: Coordinates;
  /** Azimuth towards North. */
  azimuth: number | null;
  azimuthAccuracy: number | null;
  range: number | null;
  rssi: number | null;
}

export interface SpatialLinearLocation extends LinearLocationOptions, SpatialLinearLocationOptions {
  type: LocationTypeEnum.SPATIAL_LINEAR;
}

export interface SpatialLinearLocationOptions {
  /** Tilt towards ground. */
  tilt: number | null;
  tiltPrecision: number | null;
}

export interface PolygonLocation {
  type: LocationTypeEnum.POLYGON;
  polygon: Polygon;
}

export interface SpatialLocation extends PlanarLocationOptions, SpatialLocationOptions {
  type: LocationTypeEnum.SPATIAL;
}

export enum ElevationReliabilityEnum {
  TRANSMITTED = 'TRANSMITTED',
  COMPUTED_WITH_DTM = 'COMPUTED_WITH_DTM',
  INCONSISTENT_WITH_DTM = 'INCONSISTENT_WITH_DTM',
  UNKNOWN = 'UNKNOWN',
}

export interface Elevation {
  transmitted: number | null;
  computed: number | null;
  reliability: ElevationReliabilityEnum;
}

export enum TendencyEnum {
  STEADY = 'STEADY',
  UP = 'UP',
  DOWN = 'DOWN',
}

export interface SpatialLocationOptions {
  /** Elevation Above Ground Level. Details for height. */
  elevationAgl: Elevation | null;
  /** Elevation Above Sea Level. Details for altitude. */
  elevationAsl: Elevation | null;
  /** Height from the local ground in meters. */
  height: number | null;
  /** Altitude from the sea level in meters. */
  altitude: number | null;
  verticalPrecision: number | null;
  /** Vertical speed direction towards ground. In range [-180, 180[. */
  pitch: number | null;
  /** Rotation around the front-to-back axis (longitudinal axis). . In range [-180, 180[. */
  roll: number | null;
  tendency: TendencyEnum | null;
}

export type StrobeLocation = LinearLocation | SpatialLinearLocation;
export type TargetLocation = PlanarLocation | SpatialLocation;
export type AbstractLocation = StrobeLocation | TargetLocation | TrackerLocation | CartesianLocation | PolygonLocation;

export interface PlotLight<T extends AbstractLocation> {
  id: number;
  site: string;
  sensorCode: string;
  trackId: string;
  time: string;
  identification: Identification;
  location: T | null;
  detectionTime: string;
}

export interface Plot<T extends AbstractLocation> extends PlotLight<T> {
  collaborativeData?: CollaborativeData;
  remoteData?: RemoteData;
  rfData?: RfData;
  radarData?: RadarData;
  technicalData?: TechnicalData;
}

export interface CollaborativeData {
  aeroscopeId?: string;
  aircraftId?: string;
  infodroneId?: string;
  vehicleId?: string;
  model?: string;
  fleet?: string;
  inContingency: boolean;
}

export interface RemoteData {
  droneId?: string;
  remoteId?: string;
}

export interface RfData {
  frequency?: number;
  rssi?: number;
  protocol?: string;
}

export interface RadarData {
  rcs?: number;
  doppler?: number;
  microDoppler?: number;
}

export interface ServoState {
  // Silent mode.
  servo5: number;

  // GPS L1 jamming.
  servo6: number;

  // Wifi 2.4 jamming.
  servo7: number;

  // Wifi 5.8 jamming.
  servo8: number;

  // Default GPS L1.
  servo9: number;

  // Default Wifi 2.4.
  servo10: number;

  // Default Wifi 5.8.
  servo11: number;

  //Low battery.
  servo12: number;
}

export interface EstimatedTime {
  timeToSafeReturn: number;
  timeToLand: number;
  timeToNextMissionItem: number;
  timeToMissionEnd: number;
  timeToCompleteAction: number;
}

export interface TechnicalData {
  systemId: number | null;
  autopilotId: number | null;
  batteryRemaining: number | null;
  //seconds
  timeRemaining: number | null;
  droneModel: string | null;
  flyingMode: string | null;
  homePosition: Coordinates3D | null;
  landedState: LandedStateEnum | null;
  servoState: ServoState | null;
  trackedTargetId: string | null;
  jammingTargetId: string | null;
  droneStopped: boolean;
  estimatedTime: EstimatedTime | null;
  armed: boolean;
}

export enum LandedStateEnum {
  MAV_LANDED_STATE_UNDEFINED = 'MAV_LANDED_STATE_UNDEFINED',
  MAV_LANDED_STATE_ON_GROUND = 'MAV_LANDED_STATE_ON_GROUND',
  MAV_LANDED_STATE_IN_AIR = 'MAV_LANDED_STATE_IN_AIR',
  MAV_LANDED_STATE_TAKEOFF = 'MAV_LANDED_STATE_TAKEOFF',
  MAV_LANDED_STATE_LANDING = 'MAV_LANDED_STATE_LANDING',
}

//Order is important to sort families in monitoring
export enum SensorFamilyEnum {
  COLLABORATIVE = 'COLLABORATIVE',
  RF = 'RF',
  CAMERA = 'CAMERA',
  RID = 'RID',
  RADAR = 'RADAR',
  NEUTRAL = 'NEUTRAL',
  AUTOMATON = 'AUTOMATON',
}

export interface SensorAction {
  action: ActionEnum;
  sensorFamily: SensorFamilyEnum;
}

export interface SensorConfigurationCriteria {
  site: string;
  platforms?: string[];
  active?: boolean;
  codes?: string[];
  ids?: number[];
  types?: string[];
}
